<template>
  <div>
    <v-container class="pb-0">
      <!-- Filters Row -->
      <v-row class="my-0 mb-4" align="center">
        <!-- Company Selector -->
        <v-col v-if="!companyScoped" cols="12" sm="6" md="4" class="py-2 px-2">
          <v-select
            :background-color="filters.company ? 'serenade' : ''"
            v-model="filters.company"
            :items="companies"
            :loading="companiesLoading"
            :disabled="companiesLoading"
            item-text="title"
            item-value="id"
            dense
            hide-details
            outlined
            clearable
            :placeholder="'company'|localize"
            :menu-props="{ offsetY: true, bottom: true }"
            @change="clearRequestList();getRequests();"
          >
            <template v-slot:item="{ item }">
              <v-list-item-title>{{ item.title }}</v-list-item-title>
            </template>
            <template v-slot:selection="{ item }">
              <span class="text-truncate" style="max-width: 100px">{{ item.title }}</span>
            </template>
          </v-select>
        </v-col>

        <!-- Destinations Selector -->
        <v-col cols="12" sm="6" md="4" class="py-2 px-2">
          <v-select
            :background-color="filters.destinations.length ? 'serenade' : ''"
            v-model="filters.destinations"
            :items="destinationsList"
            item-text="value"
            item-value="key"
            dense
            hide-details
            outlined
            multiple
            clearable
            :placeholder="'destinations'|localize"
            :menu-props="{ offsetY: true, bottom: true }"
            @change="clearRequestList();getRequests();"
          >
            <template v-slot:item="{ item }">
              <v-list-item-title>{{ item.value }}</v-list-item-title>
            </template>
            <template v-slot:selection="{ item, index }">
              <div class="d-flex">
                <span v-if="index === 0" class="text-truncate" style="max-width: 100px">{{ item.value }}</span>
                <span v-if="index === 1" class="grey--text text-caption">+{{ filters.destinations.length - 1 }}</span>
              </div>
            </template>
          </v-select>
        </v-col>

        <!-- Shipment Type Selector -->
        <v-col cols="12" sm="6" md="4" class="py-2 px-2">
          <v-select
            :background-color="filters.shipmentTypes.length ? 'serenade' : ''"
            v-model="filters.shipmentTypes"
            :items="shipmentTypeList"
            item-text="name"
            item-value="key"
            dense
            hide-details
            outlined
            multiple
            clearable
            :placeholder="'shipmentType'|localize"
            :menu-props="{ offsetY: true, bottom: true }"
            @change="clearRequestList();getRequests();"
          >
            <template v-slot:item="{ item }">
              <v-list-item-content class="py-1">
                <v-list-item-title class="font-weight-bold text-caption mb-0">{{ item.name }}</v-list-item-title>
                <v-list-item-subtitle class="grey--text" style="font-size: 10px; line-height: 1.1;">{{ item.description }}</v-list-item-subtitle>
              </v-list-item-content>
            </template>
            <template v-slot:selection="{ item, index }">
              <div class="d-flex">
                <span v-if="index === 0" class="text-truncate" style="max-width: 100px">{{ item.name }}</span>
                <span v-if="index === 1" class="grey--text text-caption">+{{ filters.shipmentTypes.length - 1 }}</span>
              </div>
            </template>
          </v-select>
        </v-col>

        <!-- Transport Mode Selector -->
        <v-col cols="12" sm="6" md="4" class="py-2 px-2">
          <v-select
            :background-color="filters.transportModes.length ? 'serenade' : ''"
            v-model="filters.transportModes"
            :items="transportModeList"
            item-text="text"
            item-value="value"
            dense
            hide-details
            outlined
            multiple
            clearable
            :placeholder="'transportMode'|localize"
            :menu-props="{ offsetY: true, bottom: true }"
            @change="clearRequestList();getRequests();"
          >
            <template v-slot:item="{ item }">
              <v-icon class="mr-2" color="silver-chalice">{{ TRANSPORT_ICONS[item.value] }}</v-icon>
              <v-list-item-title>{{ item.text }}</v-list-item-title>
            </template>
            <template v-slot:selection="{ item, index }">
              <v-icon v-if="index <= 2" class="mr-2" color="silver-chalice">{{ TRANSPORT_ICONS[item.value] }}</v-icon>
              <span v-if="index === 3" class="grey--text text-caption">+{{ filters.transportModes.length - 3 }}</span>
            </template>
          </v-select>
        </v-col>

        <!-- Type of Quotation Selector -->
        <v-col cols="12" sm="6" md="4" class="py-2 px-2">
          <v-select
            :background-color="filters.request_type ? 'serenade' : ''"
            v-model="filters.request_type"
            :items="requestTypesList"
            item-text="name"
            item-value="value"
            dense
            hide-details
            outlined
            :placeholder="'requestType'|localize"
            :menu-props="{ offsetY: true, bottom: true }"
            @change="clearRequestList();getRequests();"
          />
        </v-col>

        <!-- Date Range Selector -->
        <v-col cols="12" sm="6" md="4" class="py-2 px-2">
          <v-row class="my-0 mx-n1">
            <v-col cols="6" class="py-0 px-1">
              <v-menu
                ref="menuDateFrom"
                v-model="menuDateFrom"
                :close-on-content-click="false"
                :return-value.sync="filters.dateFrom"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    :background-color="filters.dateFrom ? 'serenade' : ''"
                    :placeholder="'dateFrom' | localize"
                    :value="filters.dateFrom"
                    readonly
                    dense
                    hide-details
                    outlined
                    v-on="on"
                  />
                </template>
                <v-date-picker
                  v-model="filters.dateFrom"
                  no-title
                  scrollable
                  @input="$refs.menuDateFrom.save(filters.dateFrom)"
                  @change="clearRequestList();getRequests();"
                />
              </v-menu>
            </v-col>
            <v-col cols="6" class="py-0 px-1">
              <v-menu
                ref="menuDateTo"
                v-model="menuDateTo"
                :close-on-content-click="false"
                :return-value.sync="filters.dateTo"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    :background-color="filters.dateTo ? 'serenade' : ''"
                    :placeholder="'dateTo' | localize"
                    :value="filters.dateTo"
                    readonly
                    dense
                    hide-details
                    outlined
                    v-on="on"
                  />
                </template>
                <v-date-picker
                  v-model="filters.dateTo"
                  no-title
                  scrollable
                  @input="$refs.menuDateTo.save(filters.dateTo)"
                  @change="clearRequestList();getRequests();"
                />
              </v-menu>
            </v-col>
          </v-row>
        </v-col>
      </v-row>

      <!-- KPI Cards Block -->
      <v-row v-if="hasCompanyContext && requestList && kpiCards.length" class="mx-0 my-4" justify="start">
        <v-col cols="12">
          <v-row dense class="kpi-cards-row">
            <v-col
              v-for="(card, index) in kpiCards"
              :key="index"
              cols="6"
              md="4"
              lg="2"
              class="pa-1 pa-md-2 d-flex"
            >
              <v-card
                class="kpi-card elevation-0 flex-grow-1"
                :style="{ backgroundColor: KPI_THEMES[card.theme]?.bg }"
              >
                <div class="kpi-card__body d-flex flex-column">
                  <div class="kpi-card__title">{{ card.title }}</div>

                  <div class="kpi-card__content">
                    <div
                      class="kpi-card__icon-wrap"
                      :style="{ backgroundColor: KPI_THEMES[card.theme]?.iconBg }"
                    >
                      <v-icon
                        class="kpi-card__icon"
                        :color="KPI_THEMES[card.theme]?.icon"
                      >{{ card.icon }}</v-icon>
                    </div>

                    <div class="kpi-card__value-row d-flex align-baseline">
                      <span class="kpi-card__value">{{ card.value }}</span>
                      <span v-if="card.percentage" class="kpi-card__percentage">{{ card.percentage }}</span>
                    </div>
                  </div>
                </div>
              </v-card>
            </v-col>
          </v-row>
        </v-col>
      </v-row>

      <!-- Pie Charts Block -->
      <v-row
        v-if="hasCompanyContext && requestList && !hasReportData"
        class="mx-0 my-6 align-center justify-center"
        style="min-height: 360px;"
      >
        <v-col cols="12" class="text-center grey--text text-body-1">
          {{ 'noDataAvailable' | localize }}
        </v-col>
      </v-row>

      <v-row v-else-if="hasCompanyContext && requestList" class="mx-0 my-6 justify-center">
        <v-col cols="12" md="6" class="d-flex flex-column align-center pa-4">
          <h3 class="text-subtitle-1 font-weight-bold mb-4">{{ 'requestsPerShipmentType' | localize }}</h3>

          <div class="d-flex align-center justify-center" style="min-height: 200px; width: 100%;">
            <div v-if="chartTransportModesSeries.length === 0" class="grey--text text-body-1 py-8">
              {{ 'noDataAvailable' | localize }}
            </div>
            <apexchart
              v-else
              type="pie"
              width="440"
              height="300"
              :options="chartTransportModesOptions"
              :series="chartTransportModesSeries"
            ></apexchart>
          </div>
        </v-col>

        <v-col cols="12" md="6" class="d-flex flex-column align-center pa-4">
          <h3 class="text-subtitle-1 font-weight-bold mb-4">{{ 'destinationRegions' | localize }}</h3>

          <div class="d-flex align-center justify-center" style="min-height: 200px; width: 100%;">
            <div v-if="chartRegionsSeries.length === 0" class="grey--text text-body-1 py-8">
              {{ 'noDataAvailable' | localize }}
            </div>
            <apexchart
              v-else
              type="pie"
              width="440"
              height="300"
              :options="chartRegionsOptions"
              :series="chartRegionsSeries"
            ></apexchart>
          </div>
        </v-col>
      </v-row>
    </v-container>

    <div v-if="!hasCompanyContext" class="pa-3">
      <v-alert class="my-0 text-center" dense outlined text color="grey lighten-1">{{ "transportCompanyIsNotSelected"|localize }}</v-alert>
    </div>
  </div>
</template>

<script>
  import { KPI_THEMES, getTransportModeChartColor } from '@/enums/kpiThemeColors';
  import { TRANSPORT_ICONS } from '@/enums/transportMode';
  import api from '@/api/api';
  import localize from '@/filters/localize';
  import methods from '@/helpers/methods';
  import VueApexCharts from 'vue-apexcharts';

  export default {
    name: 'AdminRequestsBookingsReports',
    props: ['companies', 'companiesLoading', 'companyScoped'],
    components: {
      apexchart: VueApexCharts
    },

    data() {
      return {
        menuDateFrom: false,
        menuDateTo: false,
        loading: false,
        filters: {
          dateFrom: methods.yearAgoDate(),
          dateTo: methods.todayDate(),
          company: null,
          destinations: [],
          shipmentTypes: [],
          transportModes: [],
          request_type: 'ready_for_collection'
        },
        requestList: null
      };
    },

    mounted() {
      if (this.companyScoped) {
        this.getRequests();
      }
    },

    computed: {
      KPI_THEMES() {
        return KPI_THEMES;
      },
      TRANSPORT_ICONS() {
        return TRANSPORT_ICONS;
      },
      COUNTRY_LIST() {
        return this.$store.getters.countries;
      },
      dictionaryLoaded() {
        return this.$store.getters.dictionaryLoaded;
      },
      requestTypesList() {
        return this.$store.getters.requestTypesList;
      },
      destinationsList() {
        return this.$store.getters.regions;
      },
      shipmentTypeList() {
        // Import Europe / Export Europe are excluded from the report filter per feedback #537.
        return this.$store.getters.countryPresets.filter(
          preset => !['import_europe', 'export_europe'].includes(preset.key)
        );
      },
      transportModeList() {
        return [
          { text: localize('road') || 'Road', value: 'road' },
          { text: localize('air') || 'Air', value: 'air' },
          { text: localize('sea') || 'Sea', value: 'sea' },
          { text: localize('rail') || 'Train', value: 'rail' }
        ];
      },
      kpiCards() {
        if (!this.requestList) return [];

        const categoryKey = this.filters.request_type === 'ready_for_collection' 
          ? 'goods_ready_for_collection' 
          : 'price_requests';

        const data = this.requestList[categoryKey] || {};
        
        const totalRequests = data.total_number_of_requests?.total || 0;
        const seaRequests = data.total_number_of_requests?.sea || 0;
        const airRequests = data.total_number_of_requests?.air || 0;
        const roadRequests = data.total_number_of_requests?.road || 0;
        const railRequests = data.total_number_of_requests?.rail || 0;
        const avgOfferAmount = data.average_amount_of_offers?.SEK?.total || 0;
        const percentOffers = data.percent_of_requests_with_offers || 0;

        const cards = [];

        // 1. Total Requests
        if (totalRequests > 0) {
          cards.push({
            title: localize('total_number_of_requests') || 'Total number of requests',
            value: totalRequests,
            icon: 'mdi-file-document-outline',
            theme: 'default',
            type: 'total'
          });
        }

        // Helper to calculate percentage of total requests
        const getPct = (val) => totalRequests > 0 ? Math.round((val / totalRequests) * 100) : 0;

        // 2. Sea Requests
        if (seaRequests > 0) {
          cards.push({
            title: localize('sea') || 'Sea',
            value: seaRequests,
            percentage: `${getPct(seaRequests)}%`,
            icon: 'mdi-ferry',
            theme: 'sea',
            type: 'transport'
          });
        }

        // 3. Air Requests
        if (airRequests > 0) {
          cards.push({
            title: localize('air') || 'Air',
            value: airRequests,
            percentage: `${getPct(airRequests)}%`,
            icon: 'mdi-airplane',
            theme: 'air',
            type: 'transport'
          });
        }

        // 4. Rail Requests
        if (railRequests > 0) {
          cards.push({
            title: localize('rail') || 'Rail',
            value: railRequests,
            percentage: `${getPct(railRequests)}%`,
            icon: 'mdi-train',
            theme: 'rail',
            type: 'transport'
          });
        }

        // 5. Road Requests
        if (roadRequests > 0) {
          cards.push({
            title: localize('road') || 'Road',
            value: roadRequests,
            percentage: `${getPct(roadRequests)}%`,
            icon: 'mdi-car',
            theme: 'road',
            type: 'transport'
          });
        }

        // 6. Avg Offer Amount
        if (avgOfferAmount > 0) {
          cards.push({
            title: localize('averageAmountOffers') || 'Average amount (offers)',
            value: `${avgOfferAmount.toLocaleString()} SEK`,
            icon: 'mdi-cash-multiple',
            theme: 'default',
            type: 'average'
          });
        }

        // 7. Percentage of requests with submitted offers
        if (percentOffers > 0) {
          const formattedPercent = Math.round(Number(percentOffers));
          cards.push({
            title: localize('percentRequestsWithOffers') || 'Percentage of requests with submitted offers',
            value: `${formattedPercent}%`,
            icon: 'mdi-percent-outline',
            theme: 'default',
            type: 'percentage'
          });
        }

        return cards;
      },
      chartTransportModesSeries() {
        const key = this.filters.request_type === 'ready_for_collection' ? 'ready_for_collection' : 'price_request';
        const data = this.requestList?.pie_charts?.[key]?.transport_mode || {};
        return Object.values(data);
      },
      chartTransportModesOptions() {
        const key = this.filters.request_type === 'ready_for_collection' ? 'ready_for_collection' : 'price_request';
        const data = this.requestList?.pie_charts?.[key]?.transport_mode || {};
        const keys = Object.keys(data);
        return {
          chart: { type: 'pie', fontFamily: 'inherit' },
          plotOptions: { pie: { customScale: 1.05 } },
          labels: keys.map(k => localize(k) || k),
          colors: keys.map(k => getTransportModeChartColor(k)),
          legend: { position: 'right', width: 170, fontFamily: 'inherit', fontSize: '12px' },
          dataLabels: { enabled: true, formatter: (val) => `${Math.round(val)}%` },
          tooltip: { y: { formatter: (val) => `${val}` } }
        };
      },
      chartRegionsSeries() {
        const key = this.filters.request_type === 'ready_for_collection' ? 'ready_for_collection' : 'price_request';
        const regionCounts = this.getRegionCounts(key);
        return Object.values(regionCounts).filter(val => val > 0);
      },
      chartRegionsOptions() {
        const key = this.filters.request_type === 'ready_for_collection' ? 'ready_for_collection' : 'price_request';
        const regionCounts = this.getRegionCounts(key);
        const activeKeys = Object.keys(regionCounts).filter(k => regionCounts[k] > 0);

        return {
          chart: { type: 'pie', fontFamily: 'inherit' },
          plotOptions: { pie: { customScale: 1.05 } },
          labels: activeKeys.map(k => localize(k) || k),
          colors: activeKeys.map(k => this.getRegionColor(k)),
          legend: { position: 'right', width: 170, fontFamily: 'inherit', fontSize: '12px' },
          dataLabels: { enabled: true, formatter: (val) => `${Math.round(val)}%` },
          tooltip: { y: { formatter: (val) => `${val}` } }
        };
      },
      hasReportData() {
        return this.kpiCards.length > 0
          || this.chartTransportModesSeries.length > 0
          || this.chartRegionsSeries.length > 0;
      },
      activeCompanyId() {
        return this.$store.getters.userData?.company?.id || null;
      },
      hasCompanyContext() {
        return this.companyScoped ? this.activeCompanyId !== null : this.filters.company !== null;
      }
    },

    methods: {
      clearRequestList() {
        this.requestList = null;
      },
      queryData() {
        let queryData = {};
        queryData.filters = {};
        if (this.filters.dateFrom || this.filters.dateTo) {
          queryData.filters.date = ["", ""];
          this.filters.dateFrom && (queryData.filters.date[0] = this.filters.dateFrom);
          this.filters.dateTo && (queryData.filters.date[1] = this.filters.dateTo);
        }
        if (this.companyScoped) {
          queryData.company_id = this.activeCompanyId;
        } else if (this.filters.company) {
          queryData.filters.company = [this.filters.company];
        }

        if (this.filters.destinations.length) {
          let countryCodes = [];
          this.filters.destinations.forEach(regionKey => {
            const matches = this.COUNTRY_LIST.filter(c => c.region_code === regionKey).map(c => c.code);
            countryCodes = countryCodes.concat(matches);
          });
          queryData.filters.destinations = countryCodes;
        }
        if (this.filters.shipmentTypes.length) {
          queryData.filters.shipment_types = this.filters.shipmentTypes;
        }
        if (this.filters.transportModes.length) {
          queryData.filters.transport_modes = this.filters.transportModes;
        }
        
        return queryData;
      },
      async getRequests() {
        if (!this.dictionaryLoaded) {
          this.$store.dispatch('loadDictionary').catch(() => {});
          return;
        }
        if (!this.hasCompanyContext) {
          this.clearRequestList();
          return;
        }
        try {
          this.loading = true;
          const performanceResponse = this.companyScoped
            ? await api.getCompanyRequestsBookingsReport(this.queryData())
            : await api.getRequestsBookings(this.queryData());
          this.setRequestsData(performanceResponse);
        } catch (e) {
          console.error(e);
        } finally {
          this.loading = false;
        }
      },
      setRequestsData(requestsData) {
        this.requestList = requestsData?.data || null;
      },
      getRegionCounts(chartKey) {
        const rawCountryData = this.requestList?.pie_charts?.[chartKey]?.destination_region || {};
        const regionCounts = { 'Europe': 0, 'Asia': 0, 'Americas (excl. USA)': 0, 'USA': 0, 'Africa': 0, 'Oceania': 0, 'Other': 0 };

        Object.entries(rawCountryData).forEach(([countryCode, count]) => {
          const match = this.COUNTRY_LIST.find(c => c.code === countryCode.toUpperCase());
          const region = (match && regionCounts[match.region] !== undefined) ? match.region : 'Other';
          regionCounts[region] += Number(count);
        });

        return regionCounts;
      },
      getRegionColor(region) {
        const colors = {
          'Europe': '#e6a100',
          'Asia': '#4d4d4d',
          'Americas (excl. USA)': '#8c7300',
          'USA': '#3d6fb3',
          'Africa': '#8e8e8e',
          'Oceania': '#a1a1a1',
          'Other': '#cccccc'
        };
        return colors[region] || '#cccccc';
      }
    },
    watch: {
      dictionaryLoaded(loaded) {
        if (loaded) this.getRequests();
      },
    },
  };
</script>

<style scoped lang="scss">
.kpi-cards-row {
  align-items: stretch;
}

.kpi-card {
  width: 100%;
  border-radius: 12px !important;
  border: none !important;
  padding: 10px 12px;

  &__body {
    gap: 8px;
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }

  &__icon-wrap {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    border-radius: 50%;
  }

  &__icon {
    font-size: 28px !important;
  }

  &__content {
    min-width: 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  &__title {
    font-size: 14px;
    font-weight: 600;
    line-height: 1.25;
    
  }

  &__value {
    font-size: 1.75rem;
    font-weight: 700;
    line-height: 1;
    color: #111827;
  }

  &__percentage {
    font-size: 14px;
    font-weight: 500;
    color: #595a5c;
    margin-left: 6px;
    line-height: 1;
  }
}
</style>
